import React from 'react';
import type { FC, ReactNode } from 'react';
import { useAsync, Spinner, Alert } from '@wix/cli-ui-kit';
import { match } from 'variant';
import { useDevCenterClient } from '@wix/dev-center-client';
import { useMetasiteManagerClient } from '@wix/metasite-manager-client';
import { useTranslation } from '@wix/cli-i18n';
import { useProjectModel } from '../../model.js';
import { useSite } from '../../site/useSite.js';
import { useDevEnvironment } from './DevEnvironment.js';

export const RequiredAppsWarning: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { logger } = useDevEnvironment();
  const { site } = useSite();
  const { model } = useProjectModel();
  const { getRequiredApps } = useDevCenterClient();
  const { getSiteInstalledApps } = useMetasiteManagerClient();
  const { t } = useTranslation();

  const { status: requiredAppsStatus } = useAsync(async () => {
    const requiredApps = await getRequiredApps(model.config.appId);

    if (requiredApps.length === 0) {
      return;
    }

    const siteInstalledApps = await getSiteInstalledApps(site.id);

    const missingRequiredApps = requiredApps.filter(
      (requiredApp) => !siteInstalledApps.includes(requiredApp.id)
    );

    if (missingRequiredApps.length === 0) {
      return;
    }

    logger.warn(
      <Alert type="warning">
        {t('dev_command.required_apps.warning', {
          requiredAppNames: missingRequiredApps
            .map((app) => app.name)
            .join(', '),
        })}
      </Alert>
    );
  }, []);

  return match(requiredAppsStatus, {
    Loading: () => <Spinner text={t('dev_command.required_apps.loading')} />,
    Error: () => null,
    Success: () => children,
  });
};
